import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Truck, ArrowRight, Loader2, Smartphone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { authApi, saveAuth } from '../lib/api';
import EmailOtpFlow from '../components/EmailOtpFlow';

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('phone'); // 'phone' | 'email'
  const [form, setForm] = useState({ phone: '', password: '' });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token, user } = await authApi.login(form);
      saveAuth(token, user);
      toast({ title: 'Welcome back!', description: `Signed in as ${user.name}.` });
      navigate('/app/dashboard');
    } catch (err) {
      toast({ title: 'Login failed', description: err?.response?.data?.detail || 'Invalid credentials' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-[#0B2545] p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20"></div>
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#00A651]/20 rounded-full blur-3xl"></div>
        <Link to="/" className="flex items-center gap-2 relative z-10">
          <div className="w-10 h-10 rounded-lg brand-gradient flex items-center justify-center">
            <Truck className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-bold text-2xl text-white font-[Poppins]">EZLOAD</span>
        </Link>
        <div className="relative z-10">
          <h2 className="text-4xl font-extrabold text-white leading-tight mb-4 font-[Poppins]">Move freight across India, effortlessly.</h2>
          <p className="text-white/70">50,000+ verified truckers. GST-ready. UPI payments. All in one app.</p>
        </div>
        <div className="relative z-10 text-xs text-white/40">© 2025 EZLOAD Technologies Pvt. Ltd.</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-lg brand-gradient flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-xl text-[#0B2545] font-[Poppins]">EZLOAD</span>
          </Link>
          <h1 className="text-3xl font-extrabold text-[#0B2545] font-[Poppins] mb-2">Welcome back</h1>
          <p className="text-gray-500 mb-6">Sign in to your EZLOAD account.</p>

          {/* Tabs */}
          <div className="grid grid-cols-2 gap-2 mb-6 p-1 bg-gray-100 rounded-lg">
            <button
              data-testid="login-tab-phone"
              type="button"
              onClick={() => setTab('phone')}
              className={`h-9 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition ${tab === 'phone' ? 'bg-white shadow text-[#0B2545]' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Smartphone className="w-4 h-4" /> Phone
            </button>
            <button
              data-testid="login-tab-email"
              type="button"
              onClick={() => setTab('email')}
              className={`h-9 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition ${tab === 'email' ? 'bg-white shadow text-[#0B2545]' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Mail className="w-4 h-4" /> Email OTP
            </button>
          </div>

          {tab === 'phone' ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Mobile Number</Label>
                <Input data-testid="login-phone-input" type="tel" required placeholder="+91 98765 43210" className="h-11 mt-1.5" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <div className="flex justify-between items-center">
                  <Label className="text-sm font-medium">Password</Label>
                  <a href="#" className="text-xs text-[#00A651] font-medium">Forgot?</a>
                </div>
                <Input data-testid="login-password-input" type="password" required placeholder="••••••••" className="h-11 mt-1.5" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
              </div>
              <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full h-11 bg-[#00A651] hover:bg-[#008a43] text-white">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</> : <>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </form>
          ) : (
            <EmailOtpFlow mode="login" navigateTo="/app/dashboard" />
          )}

          <p className="text-sm text-center text-gray-500 mt-6">
            New to EZLOAD? <Link to="/signup" className="text-[#00A651] font-semibold">Create account</Link>
          </p>
          <p className="text-xs text-center text-gray-400 mt-2">
            Are you a driver? <Link to="/driver/login" className="text-[#0B2545] font-semibold">Sign in with OTP →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
